import { z } from 'zod';

/**
 * The identity signals the ban-evasion pipeline knows how to capture and match
 * (N19, `03-trust-ban-evasion.md` §signal-type registry).
 *
 * **Owner decision, 2026-07-22: raw biometrics are excluded.** Mobile OS
 * biometric sensors never hand fingerprint data to an app, and nothing here
 * stores a raw document number, a raw device id or a raw address either — only
 * a peppered hash or an embedding, neither of which is reversible to the value
 * it was made from.
 *
 * **Why the list lives in contracts and the capture wiring does not.** The set
 * of types is read by three surfaces: the api's matcher, the `trust.signals`
 * settings record, and P14b's policy editor, which draws one row per registered
 * type. That is U3's definition of a shared contract. *How* a value is obtained
 * — a request header, an AI vector, a profile answer — is api-only knowledge and
 * stays in `modules/trust/registries/<type>.signal.ts`. Adding a signal type is
 * therefore one entry here plus one file there, and match logic, the settings
 * surface and the review queue pick it up with no further edit.
 */
export const IDENTITY_SIGNAL_TYPES = [
  'device_fingerprint',
  'face_embedding',
  'id_document_number_hash',
  'email_hash',
  'phone_hash',
] as const;

export const IdentitySignalTypeSchema = z.enum(IDENTITY_SIGNAL_TYPES);

export type IdentitySignalType = (typeof IDENTITY_SIGNAL_TYPES)[number];

/**
 * Where the pipeline runs. The first two are **mandatory** capture points under
 * N19: a code path that registers an account or submits a case without running
 * the pipeline is a rule violation the verifier greps for. `contact_verification`
 * (P30b2) is where a phone number is claimed and proven — screened when it is
 * submitted, committed when it is confirmed. Appended last: the pg enum is
 * ordered, and a value added in the middle emits `BEFORE`.
 */
export const CAPTURE_POINTS = [
  'registration',
  'verification_submission',
  'contact_verification',
] as const;

export const CapturePointSchema = z.enum(CAPTURE_POINTS);

export type CapturePoint = (typeof CAPTURE_POINTS)[number];

/**
 * How a signal's value is kept. `hash` is sha256 over the normalized value plus
 * the server pepper; `embedding` is a pgvector row. Neither is reversible, which
 * is the whole privacy stance rather than an implementation detail.
 */
export const SIGNAL_STORAGES = ['hash', 'embedding'] as const;

export const SignalStorageSchema = z.enum(SIGNAL_STORAGES);

export type SignalStorage = (typeof SIGNAL_STORAGES)[number];

/** How two stored signals are compared. */
export const SIGNAL_MATCHERS = ['equality', 'cosine'] as const;

export const SignalMatcherSchema = z.enum(SIGNAL_MATCHERS);

export type SignalMatcher = (typeof SIGNAL_MATCHERS)[number];

/** What the registry declares about one signal type. */
export interface IdentitySignalDescriptor {
  type: IdentitySignalType;
  storage: SignalStorage;
  matcher: SignalMatcher;
  capturePoints: readonly CapturePoint[];
}

export const IDENTITY_SIGNAL_DESCRIPTORS: Readonly<
  Record<IdentitySignalType, IdentitySignalDescriptor>
> = {
  /** The mobile device-info composite, sent as a header at signup. */
  device_fingerprint: {
    type: 'device_fingerprint',
    storage: 'hash',
    matcher: 'equality',
    capturePoints: ['registration'],
  },
  /**
   * The selfie embedding, reused from the advisory face-match step so the
   * platform pays for **one** AI call rather than two (N15).
   *
   * Captured at submission only — there is no selfie before a case exists.
   */
  face_embedding: {
    type: 'face_embedding',
    storage: 'embedding',
    matcher: 'cosine',
    capturePoints: ['verification_submission'],
  },
  /**
   * The normalized Civil-Registry-ID answer.
   *
   * Declared at **both** points, per the domain plan, even though the answer
   * cannot exist at signup: A4 makes the Civil Registry ID a registration-flow
   * *question*, answered after the account is created. A capture that finds no
   * value is a no-op, not an error, so declaring the earlier point costs
   * nothing and is what makes the day the answer moves earlier a zero-line
   * change here.
   */
  id_document_number_hash: {
    type: 'id_document_number_hash',
    storage: 'hash',
    matcher: 'equality',
    capturePoints: ['registration', 'verification_submission'],
  },
  /** The normalized address — catches a deleted-then-recreated account. */
  email_hash: {
    type: 'email_hash',
    storage: 'hash',
    matcher: 'equality',
    capturePoints: ['registration'],
  },
  /**
   * The normalized phone number — the one the account proves at contact
   * verification (P30b2), or a staff-defined phone answer where a deployment
   * still asks one at registration.
   */
  phone_hash: {
    type: 'phone_hash',
    storage: 'hash',
    matcher: 'equality',
    capturePoints: ['registration', 'contact_verification'],
  },
};

const TYPE_SET: ReadonlySet<string> = new Set<string>(IDENTITY_SIGNAL_TYPES);

/**
 * Narrows a stored or configured string.
 *
 * **Fail-closed**, and it matters more here than in most narrowings: an
 * unregistered type has no descriptor, so it has no matcher, no storage shape
 * and no capture wiring. Treating one as valid would invent all three.
 */
export function isIdentitySignalType(value: string): value is IdentitySignalType {
  return TYPE_SET.has(value);
}

export function descriptorFor(type: IdentitySignalType): IdentitySignalDescriptor {
  return IDENTITY_SIGNAL_DESCRIPTORS[type];
}

/**
 * Whether this type's `threshold` setting means anything.
 *
 * Derived from the matcher rather than declared beside it, so the two can never
 * disagree: an exact hash either equals or it does not, and P14b's policy editor
 * draws a similarity control **only** where this is true. The `trust.signals`
 * definition pins `threshold` at 1 for the rest.
 */
export function isFuzzySignal(type: IdentitySignalType): boolean {
  return IDENTITY_SIGNAL_DESCRIPTORS[type].matcher === 'cosine';
}

export function isCapturedAt(type: IdentitySignalType, point: CapturePoint): boolean {
  return IDENTITY_SIGNAL_DESCRIPTORS[type].capturePoints.includes(point);
}

/** Every type this capture point runs — the pipeline's work list (N19). */
export function signalTypesCapturedAt(point: CapturePoint): readonly IdentitySignalType[] {
  return IDENTITY_SIGNAL_TYPES.filter((type) => isCapturedAt(type, point));
}
